import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ShieldCheck, RefreshCcw, Lock, Globe2, FileCheck2, BadgeCheck } from "lucide-react";
import { KineticLines, Reveal, Eyebrow } from "@/components/site/Reveal";

const STEPS = [
  { t: "Firmographic & Account Matching", d: "Automated cross-check against target domain lists, industry SIC/NAICS codes, revenue thresholds, and location criteria." },
  { t: "Job Function & Seniority Verification", d: "Confirmation that contact title, reporting structure, and decision-making authority align strictly with client campaign specifications." },
  { t: "Direct-Dial & Telephony Validation", d: "Live telecom ping and switchboard verification to confirm active corporate extension or direct line connectivity." },
  { t: "Corporate Email & Domain Authentication", d: "Real-time SMTP handshake, MX record testing, syntax validation, and spam-trap exclusion to ensure 0% bounce probability." },
  { t: "Digital Engagement & Telemetry Capture", d: "Audit of content asset download, time on page, interaction depth, and explicit consent timestamp." },
  { t: "Human Quality Assurance (Tele-Review)", d: "Our dedicated in-house QA team reviews interaction transcripts or conducts discreet tele-verification to confirm criteria accuracy." },
  { t: "Final Data Hygiene & Delivery Sanitization", d: "Automated deduplication against client CRM suppression lists and secure, encrypted delivery via API, webhook, or secured CSV/XLSX." },
];

const COMPLIANCE = [
  { icon: Globe2, t: "GDPR — EU & UK", d: "All European prospect engagement follows strict lawful basis protocols. Verifiable opt-in consent trails, straightforward unsubscribe mechanisms, and immediate Right-to-be-Forgotten deletion workflows." },
  { icon: FileCheck2, t: "CCPA / CPRA — California", d: "Full transparency regarding consumer data collection, clear 'Do Not Sell or Share My Personal Information' mechanisms, and rigorous data minimization practices." },
  { icon: ShieldCheck, t: "CASL — Canada", d: "Explicit opt-in management, sender identity transparency, and rapid unsubscribe processing for all Canadian corporate contacts." },
  { icon: Lock, t: "CAN-SPAM & Global Standards", d: "Full adherence to commercial communication standards, physical business address inclusion, and accurate subject header protocols." },
];

export default function Quality() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    document.title = "Data Quality Guarantee & Global Compliance Standards | Vyacomm Media";
  }, []);

  return (
    <div data-testid="quality-page">
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pb-24 pt-[200px]" data-testid="quality-hero">
        <div className="pointer-events-none absolute -right-40 top-16 h-[420px] w-[420px] rounded-full bg-cobalt/20 blur-[140px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal delay={0.05} y={16}>
            <Eyebrow dark>Quality, Methodology & Compliance</Eyebrow>
          </Reveal>
          <h1 className="mt-6 max-w-4xl font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <KineticLines lines={["Zero Lead Waste.", "Verifiable Authenticity."]} accentIndex={1} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              In an era of automated scrapers and low-quality data vendors, Vyacomm Media takes a stand for purity. Every
              single lead delivered through our programs undergoes our proprietary 7-stage validation matrix.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-flame/40 bg-flame/10 px-5 py-2.5">
              <BadgeCheck className="h-4 w-4 text-flame" />
              <span className="font-mono2 text-[11px] tracking-[0.18em] text-flame">99.4% ALLOCATION FULFILLMENT · 100% REPLACEMENT GUARANTEE</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7-STAGE MATRIX */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="validation-matrix">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>The Vyacomm 7-Stage Lead Validation Matrix</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Seven gates between raw engagement and your CRM
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="space-y-2">
                {STEPS.map((s, i) => (
                  <button
                    key={s.t}
                    data-testid={`matrix-step-${i + 1}`}
                    onClick={() => setStep(i)}
                    className={`flex w-full items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all duration-300 ${
                      step === i
                        ? "border-cobalt bg-navy text-white shadow-[0_16px_40px_-18px_rgba(11,25,44,0.5)]"
                        : "border-line bg-white text-charcoal hover:border-cobalt/50"
                    }`}
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full font-mono2 text-xs font-semibold ${
                        step === i ? "bg-flame text-navy" : "bg-canvas text-cobalt"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="text-sm font-semibold">{s.t}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="grain relative flex h-full min-h-[320px] flex-col justify-between overflow-hidden rounded-3xl bg-navy p-9 text-white sm:p-12"
                  data-testid="matrix-detail-panel"
                >
                  <div>
                    <span className="font-jakarta text-7xl font-extrabold text-white/10">0{step + 1}</span>
                    <h3 className="mt-2 font-jakarta text-2xl font-extrabold sm:text-3xl">{STEPS[step].t}</h3>
                    <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300">{STEPS[step].d}</p>
                  </div>
                  <div className="mt-8">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-cobalt to-flame"
                        initial={{ width: 0 }}
                        animate={{ width: `${((step + 1) / 7) * 100}%` }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                    <p className="mt-3 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">
                      STAGE {step + 1} OF 7 — {Math.round(((step + 1) / 7) * 100)}% VALIDATED
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="bg-white py-24 sm:py-32" data-testid="compliance-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Global Regulatory Compliance & Privacy Framework</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Absolute certainty that your brand is protected
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
              Marketing into international markets requires an uncompromising commitment to global data privacy
              regulations. Complete opt-in audit trails and time-stamped consent records are available for every delivery.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2">
            {COMPLIANCE.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-line bg-canvas p-8 transition-all duration-300 hover:-translate-y-1 hover:border-cobalt/50 hover:bg-white">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-eblue/10 text-cobalt transition-colors duration-300 group-hover:bg-cobalt group-hover:text-white">
                    <c.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-jakarta text-lg font-bold text-navy">{c.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-28" data-testid="guarantee-section">
        <div className="pointer-events-none absolute -left-32 bottom-0 h-[360px] w-[360px] rounded-full bg-flame/15 blur-[130px]" />
        <div className="relative z-10 mx-auto grid max-w-[1808px] items-center gap-10 px-5 sm:px-8 lg:px-12 2xl:px-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <RefreshCcw className="h-6 w-6 text-flame" />
              <Eyebrow dark>The Vyacomm Lead Replacement Guarantee</Eyebrow>
            </div>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              If a lead fails the spec, we replace it. No questions asked.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
              We stand behind our deliverables with a contractual 100% Quality & Replacement Guarantee. If any lead
              fails to meet the mutually signed campaign criteria — an incorrect job title, an invalid email bounce, or
              an out-of-spec company size — simply submit it within 14 calendar days for an immediate replacement.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4">
            <div className="rounded-3xl border border-flame/40 bg-flame/10 p-8 text-center">
              <p className="font-jakarta text-6xl font-extrabold text-flame">100%</p>
              <p className="mt-2 font-mono2 text-[10px] tracking-[0.22em] text-slate-300">QUALITY & REPLACEMENT GUARANTEE</p>
              <Link
                to="/contact-us"
                data-testid="quality-cta-btn"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-cobalt px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#1d4fd7]"
              >
                Put Us To The Test
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
